'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { ApiError, api, type AdminDashboard } from '@/lib/api';
import { STATUS_LABEL, STATUS_TONE, formatRupiah } from '@/components/order/labels';

const REFRESH_MS = 60_000;

function Tile({
  label,
  value,
  hint,
  href,
  alert = false,
}: {
  label: string;
  value: string;
  hint?: string;
  href?: string;
  alert?: boolean;
}) {
  const body = (
    <>
      <p className="text-[12px] font-bold text-charcoal/75">{label}</p>
      <b
        className={`mt-1 block font-display text-[2.2rem] leading-none ${alert ? 'text-redline' : 'text-charcoal'}`}
      >
        {value}
      </b>
      {hint ? <p className="mt-1.5 text-[11.5px] text-charcoal/75">{hint}</p> : null}
    </>
  );
  return href ? (
    <Link href={href} className="rounded-md bg-white p-5 transition-shadow hover:shadow-md">
      {body}
    </Link>
  ) : (
    <div className="rounded-md bg-white p-5">{body}</div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-md bg-white p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-display text-[20px] text-charcoal">{title}</h2>
        {action}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/** "3 jam", "2 hari": cukup kasar untuk memberi rasa urgensi, bukan jam pasti. */
function roughDuration(ms: number): string {
  const minutes = Math.max(1, Math.round(Math.abs(ms) / 60_000));
  if (minutes < 60) return `${minutes} menit`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} jam`;
  return `${Math.round(hours / 24)} hari`;
}

export default function DashboardPage() {
  const [data, setData] = useState<AdminDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((): void => {
    api.dashboard
      .summary()
      .then((next) => {
        setData(next);
        setError(null);
      })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.displayMessage : 'Ringkasan gagal dimuat.'),
      );
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const now = data ? new Date(data.generatedAt).getTime() : Date.now();
  const n = (value: number | undefined): string => (data ? String(value ?? 0) : '…');
  const queue = data ? data.orders.paid + data.orders.inReview : 0;

  return (
    <div className="h-full overflow-auto p-7">
      <h1 className="font-display text-[clamp(1.7rem,2.4vw,2.3rem)] leading-none text-charcoal">
        Dashboard
      </h1>
      <p className="mt-1.5 text-[13px] text-charcoal/75">
        Ringkasan pengelolaan, diperbarui tiap menit.
      </p>
      {error ? <p className="mt-3 text-[13px] text-redline">{error}</p> : null}

      {data?.revenue ? (
        <div className="mt-6 grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(220px,1fr))]">
          <Tile
            label="Omzet hari ini"
            value={formatRupiah(data.revenue.today.amount)}
            hint={`${data.revenue.today.orders} pesanan membayar`}
            href="/laporan"
          />
          <Tile
            label="Omzet 7 hari"
            value={formatRupiah(data.revenue.last7Days.amount)}
            hint={`${data.revenue.last7Days.orders} pesanan membayar`}
            href="/laporan"
          />
          <Tile
            label="Omzet bulan ini"
            value={formatRupiah(data.revenue.thisMonth.amount)}
            hint={`Uang masuk dari ${data.revenue.thisMonth.orders} pesanan · termasuk ongkir`}
            href="/laporan"
          />
        </div>
      ) : null}

      <div className="mt-3 grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(180px,1fr))]">
        <Tile
          label="Pengunjung hari ini"
          value={n(data?.visitors.today)}
          hint={
            data
              ? `${data.visitors.todayViews} halaman dibuka · kemarin ${data.visitors.yesterday} · rincian: tanya Imma`
              : 'Memuat…'
          }
          href="/asisten"
        />
        <Tile
          label="Perlu ditangani"
          value={n(queue)}
          hint="Dibayar atau menunggu desain"
          href="/pesanan?status=PAID"
          alert={queue > 0}
        />
        {(data?.orders.imajiProStuck ?? 0) > 0 ? (
          <Tile
            label="Gagal kirim ke ImajiPRO"
            value={n(data?.orders.imajiProStuck)}
            hint="Antrean berhenti mencoba; kirim ulang dari detail pesanan"
            href="/pesanan?imajipro=stuck"
            alert
          />
        ) : null}
        {(data?.orders.paymentIssues ?? 0) > 0 ? (
          <Tile
            label="Pembayaran perlu dicek"
            value={n(data?.orders.paymentIssues)}
            hint="Kelebihan bayar, bayar setelah batal, atau status belum pasti"
            href="/pesanan?bayar=bermasalah"
            alert
          />
        ) : null}
        <Tile
          label="Menunggu persetujuan desain"
          value={n(data?.orders.designPendingApproval)}
          hint="Sudah dibayar, desain belum dicek"
          href="/pesanan?desain=pending"
          alert={(data?.orders.designPendingApproval ?? 0) > 0}
        />
        <Tile
          label="Diproduksi"
          value={n(data?.orders.inProduction)}
          href="/pesanan?status=IN_PRODUCTION"
        />
        <Tile label="Dikirim" value={n(data?.orders.shipped)} href="/pesanan?status=SHIPPED" />
        <Tile
          label="Menunggu bayar"
          value={n(data?.orders.pendingPayment)}
          hint="Hangus otomatis setelah 24 jam"
          href="/pesanan?status=PENDING_PAYMENT"
        />
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Section
          title="Antrean pesanan"
          action={
            <Link href="/pesanan?status=PAID" className="text-[12px] font-semibold text-cobalt">
              Semua pesanan
            </Link>
          }
        >
          {!data ? (
            <p className="text-[12.5px] text-charcoal/60">Memuat…</p>
          ) : data.needsAction.length === 0 ? (
            <p className="text-[12.5px] text-charcoal/70">
              Tidak ada pesanan yang menunggu. Semua sudah diproses.
            </p>
          ) : (
            <ul className="divide-y divide-black/8">
              {data.needsAction.map((order) => (
                <li key={order.number}>
                  <Link
                    href={`/pesanan/${order.number}`}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-[12.5px] hover:bg-black/[0.02]"
                  >
                    <b className="w-[120px] shrink-0 text-charcoal">{order.number}</b>
                    <span className="min-w-0 flex-1 truncate text-charcoal/80">
                      {order.customerName}
                    </span>
                    <span
                      className={`rounded-md px-2 py-0.5 text-[10.5px] font-semibold ${STATUS_TONE[order.status]}`}
                    >
                      {STATUS_LABEL[order.status]}
                    </span>
                    {order.itemsWithDesign > 0 ? (
                      <span
                        className={`rounded-md px-2 py-0.5 text-[10.5px] font-semibold ${
                          order.itemsDesignApproved === order.itemsWithDesign
                            ? 'bg-lime/40 text-charcoal'
                            : 'bg-charcoal/8 text-charcoal/75'
                        }`}
                      >
                        {order.itemsDesignApproved === order.itemsWithDesign
                          ? order.itemsWithDesign === order.itemCount
                            ? 'Desain disetujui'
                            : `${order.itemsDesignApproved} desain disetujui`
                          : `${order.itemsDesignApproved}/${order.itemsWithDesign} desain disetujui`}
                      </span>
                    ) : null}
                    {order.itemsWithoutDesign > 0 ? (
                      <span className="rounded-md bg-redline/10 px-2 py-0.5 text-[10.5px] font-semibold text-redline">
                        {order.itemsWithoutDesign === order.itemCount
                          ? 'Belum ada desain'
                          : `${order.itemsWithoutDesign}/${order.itemCount} item tanpa desain`}
                      </span>
                    ) : null}
                    <span className="w-[92px] text-right text-charcoal/65">
                      {order.paidAt
                        ? `${roughDuration(now - new Date(order.paidAt).getTime())} lalu`
                        : '—'}
                    </span>
                    <b className="w-[96px] text-right text-charcoal">{formatRupiah(order.total)}</b>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Section>

        <div className="flex flex-col gap-4">
          <Section title="Hampir hangus">
            {!data ? (
              <p className="text-[12.5px] text-charcoal/60">Memuat…</p>
            ) : data.expiringSoon.length === 0 ? (
              <p className="text-[12.5px] text-charcoal/70">
                Tidak ada tagihan yang hangus dalam {data.expiringWindowHours} jam ke depan.
              </p>
            ) : (
              <ul className="divide-y divide-black/8">
                {data.expiringSoon.map((order) => (
                  <li key={order.number}>
                    <Link
                      href={`/pesanan/${order.number}`}
                      className="flex items-center gap-3 py-2 text-[12.5px]"
                    >
                      <span className="min-w-0 flex-1">
                        <b className="block truncate text-charcoal">{order.customerName}</b>
                        <span className="text-[11.5px] text-charcoal/65">
                          {order.number}
                          {order.phone ? ` · ${order.phone}` : ''}
                        </span>
                      </span>
                      <span className="text-right">
                        <b className="block text-charcoal">{formatRupiah(order.total)}</b>
                        <span className="text-[11.5px] font-semibold text-redline">
                          {order.expiresAt
                            ? `${roughDuration(new Date(order.expiresAt).getTime() - now)} lagi`
                            : ''}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section
            title="Stok menipis"
            action={
              data && data.stock.negative > 0 ? (
                <span className="rounded bg-redline/10 px-1.5 py-px text-[11px] font-bold text-redline">
                  {data.stock.negative} varian minus
                </span>
              ) : null
            }
          >
            {!data ? (
              <p className="text-[12.5px] text-charcoal/60">Memuat…</p>
            ) : data.stock.items.length === 0 ? (
              <p className="text-[12.5px] text-charcoal/70">
                Tidak ada varian berstok dengan sisa {data.stock.threshold} unit atau kurang.
              </p>
            ) : (
              <>
                <ul className="divide-y divide-black/8">
                  {data.stock.items.map((item) => (
                    <li key={`${item.productSlug}|${item.variantLabel ?? ''}|${item.sku ?? ''}`}>
                      <Link
                        href={`/produk/${item.productSlug}`}
                        className="flex items-center gap-3 py-2 text-[12.5px]"
                      >
                        <span className="min-w-0 flex-1">
                          <b className="block truncate text-charcoal">{item.productName}</b>
                          <span className="block truncate text-[11.5px] text-charcoal/65">
                            {[item.variantLabel, item.sku].filter(Boolean).join(' · ') ||
                              'Tanpa variasi'}
                          </span>
                        </span>
                        <b
                          className={`text-right ${item.stock <= 0 ? 'text-redline' : 'text-charcoal'}`}
                        >
                          {item.stock < 0 ? `${item.stock} (minus)` : item.stock}
                        </b>
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-[11.5px] text-charcoal/65">
                  {data.stock.low > data.stock.items.length
                    ? `${data.stock.items.length} dari ${data.stock.low} varian ditampilkan. `
                    : ''}
                  Batas menipis {data.stock.threshold} unit, diatur di Parameter Sistem. Minus
                  berarti terjual melebihi catatan stok.
                </p>
              </>
            )}
          </Section>

          <Section title="Katalog & konten">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
              <dt className="text-charcoal/70">Produk tayang</dt>
              <dd className="text-right font-bold text-charcoal">{n(data?.catalog.published)}</dd>
              <dt className="text-charcoal/70">Produk draf</dt>
              <dd className="text-right font-bold text-charcoal">{n(data?.catalog.draft)}</dd>
              <dt className="text-charcoal/70">Produk tanpa foto</dt>
              <dd
                className={`text-right font-bold ${data && data.catalog.withoutPhoto > 0 ? 'text-redline' : 'text-charcoal'}`}
              >
                {n(data?.catalog.withoutPhoto)}
              </dd>
              <dt className="text-charcoal/70">Artikel tayang</dt>
              <dd className="text-right font-bold text-charcoal">{n(data?.content.published)}</dd>
              <dt className="text-charcoal/70">Artikel draf</dt>
              <dd className="text-right font-bold text-charcoal">{n(data?.content.draft)}</dd>
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              <Link
                href="/artikel/baru"
                className="rounded-md border-[1.5px] border-black/12 px-4 py-2 text-[12.5px] font-semibold text-charcoal/90 hover:border-black/30"
              >
                Tulis artikel
              </Link>
              <Link
                href="/produk"
                className="rounded-md border-[1.5px] border-black/12 px-4 py-2 text-[12.5px] font-semibold text-charcoal/90 hover:border-black/30"
              >
                Kelola produk
              </Link>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
